import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'react-toastify';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../config/firebase';

interface ForgotPasswordForm {
  email: string;
}

const ForgotPasswordPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<ForgotPasswordForm>();

  const onSubmit = async (data: ForgotPasswordForm) => {
    setIsLoading(true);
    try {
      // Send password reset email directly through Firebase Auth
      await sendPasswordResetEmail(auth, data.email);
      setEmailSent(true);
      toast.success('Email de recuperação enviado com sucesso!');
    } catch (error: any) {
      console.error('Error:', error);
      switch (error.code) {
        case 'auth/invalid-email':
          toast.error('Email inválido');
          break;
        case 'auth/user-not-found':
          // Generic message for security
          toast.error('Se existe uma conta com este email, você receberá as instruções de recuperação');
          setEmailSent(true);
          break;
        case 'auth/too-many-requests':
          toast.error('Muitas tentativas. Tente novamente mais tarde');
          break;
        case 'auth/missing-android-pkg-name':
        case 'auth/missing-continue-uri':
        case 'auth/missing-ios-bundle-id':
        case 'auth/invalid-continue-uri':
        case 'auth/unauthorized-continue-uri':
          toast.error('Erro de configuração. Por favor, contate o suporte.');
          break;
        default:
          toast.error('Erro ao enviar email de recuperação');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-md mx-auto">
          <Link to="/login" className="inline-flex items-center text-sm text-neutral-600 hover:text-primary-600 mb-8">
            <ArrowLeft size={16} className="mr-1" />
            Voltar para login
          </Link>

          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-display font-bold text-neutral-900 mb-2">
                Recuperar Senha
              </h1>
              <p className="text-neutral-600">
                Digite seu email para receber as instruções de recuperação de senha
              </p>
            </div>

            {!emailSent ? (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail size={18} className="text-neutral-500" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      className="block w-full pl-10 pr-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                      {...register('email', {
                        required: 'Email é obrigatório',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Email inválido'
                        }
                      })}
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {isLoading ? (
                    <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  ) : (
                    'Enviar Email de Recuperação'
                  )}
                </button>
              </form>
            ) : (
              <div className="text-center">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <p className="text-green-800">
                    Se existe uma conta com este email, você receberá as instruções para redefinir sua senha.
                    Por favor, verifique sua caixa de entrada.
                  </p>
                </div>
                <p className="text-sm text-neutral-600 mb-4">
                  Não recebeu o email? Verifique sua pasta de spam ou tente novamente.
                </p>
                <div className="space-y-3">
                  <button
                    onClick={() => setEmailSent(false)}
                    className="w-full py-2 px-4 border border-neutral-300 rounded-md shadow-sm text-sm font-medium text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Tentar Novamente
                  </button>
                  <Link
                    to="/login"
                    className="inline-block w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Voltar para Login
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;